import { shopAgainActions } from "@redotech/redo-return-core-common/shop-again-action";
import { isDefined } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { GiftCardRecipientEnum } from "../automation";
import type {
  OperationsSettingsPatch,
  ReturnsOperationsSettingsSource,
} from "./operations-settings-source";

export const storeCreditSettingsShape = {
  storeCredit: z.object({
    type: z.enum(shopAgainActions),
    riseTriggerUrl: z.string(),
    giftCardOrderProductId: z.string(),
    hideGiftCardBranding: z.boolean(),
    disableRiseCreditEmail: z.boolean(),
    /** Stored on `team.automation`, outside `settings`; the aggregator writes it. */
    giftCardRecipient: createEnum(GiftCardRecipientEnum),
  }),
};

export type StoreCreditSettings = z.infer<
  z.ZodObject<typeof storeCreditSettingsShape>
>;

export function storeCreditSettingsFromTeam({
  settings,
  automation,
}: ReturnsOperationsSettingsSource): StoreCreditSettings {
  return {
    storeCredit: {
      type: settings.shopAgainAction ?? "gift_card",
      riseTriggerUrl: settings.riseTrigger?.url ?? "",
      giftCardOrderProductId: settings.giftCardOrderProductId ?? "",
      hideGiftCardBranding: settings.returns?.hideGiftCardBranding ?? false,
      disableRiseCreditEmail: settings.returns?.disableRiseCreditEmail ?? false,
      giftCardRecipient:
        automation?.giftCardRecipient ?? GiftCardRecipientEnum.RECIPIENT,
    },
  };
}

export function storeCreditTeamPatch(
  { settings }: ReturnsOperationsSettingsSource,
  next: StoreCreditSettings,
): OperationsSettingsPatch {
  const { storeCredit } = next;
  const set: Record<string, unknown> = {
    shopAgainAction: storeCredit.type,
    "returns.hideGiftCardBranding": storeCredit.hideGiftCardBranding,
    "returns.disableRiseCreditEmail": storeCredit.disableRiseCreditEmail,
  };
  const unset: string[] = [];

  const riseTriggerUrl = storeCredit.riseTriggerUrl.trim();
  if (riseTriggerUrl) {
    set.riseTrigger = { url: riseTriggerUrl };
  } else if (isDefined(settings.riseTrigger)) {
    unset.push("riseTrigger");
  }

  const giftCardOrderProductId = storeCredit.giftCardOrderProductId.trim();
  if (giftCardOrderProductId) {
    set.giftCardOrderProductId = giftCardOrderProductId;
  } else if (isDefined(settings.giftCardOrderProductId)) {
    unset.push("giftCardOrderProductId");
  }

  return { set, unset };
}
